import {
    IconDefinition,
    faBoxCheck,
    faChartBar,
    faCommentsAlt,
    faFileInvoice,
    faFileSignature,
    faFolders,
    faHammer,
    faHandshake,
    faHouse,
    faInventory,
    faListTimeline,
    faMoneyBillWave,
    faPenRuler,
    faQuestionSquare,
    faRulerCombined,
    faSwatchbook,
    faTableCells,
    faTableCellsLarge,
} from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { Icon, ProjectIcon } from '@/components/Icons';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { IState } from '@/state/IState';
import { CommentThreadType, DocumentStatus, ProjectMemberLiteDTO } from '@/types';
import { useAsyncState, useUnreadThreadCount } from '@/utils/hooks';
import { classnames } from '@/utils/html';
import theme from './SellerSidebar.module.scss';
import { hasConfig } from '@/services/booking';
import { Menu } from '@ygtechnologies/rubus-ui';
import {
    BygrModule,
    CustomLink,
    ProjectFeatureToggleFlags,
    useProjectHasFeatureToggle,
    useProfileHasFeatureToggle,
    ProfileFeatureToggleFlags,
} from '@ygtechnologies/bygr-frontend-component-library';
import { faCorner, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { SellerSidebarReportsPreview } from './SellerSidebarReportsPreview';
import { getProjectMemberLite } from '@/services/project-member';
import { ProjectPermissionToggle } from '@/components/permissions';
import { SellerSidebarCustomerSurveyPreview } from './SellerSidebarCustomerSurveyPreview';
import { AddButton } from './AddButton';
import { searchResidentialUnitDocuments, searchProjectDocuments } from '@/services/documents';

const getProjectMemberStatus = (data: ProjectMemberLiteDTO[], currentProjectId: number | undefined) => {
    return {
        [BygrModule.ChangeManagement]: {
            hasModule: data.some((x) => x.bygrModules.includes(BygrModule.ChangeManagement)),
            hasProject: data.some(
                (x) => x.projectId === currentProjectId && x.bygrModules.includes(BygrModule.ChangeManagement)
            ),
        },
        [BygrModule.Aftermarket]: {
            hasModule: data.some((x) => x.bygrModules.includes(BygrModule.Aftermarket)),
            hasProject: data.some((x) => x.projectId === currentProjectId && x.bygrModules.includes(BygrModule.Aftermarket)),
        },
        [BygrModule.QualityManagement]: {
            hasModule: data.some((x) => x.bygrModules.includes(BygrModule.QualityManagement)),
            hasProject: data.some(
                (x) => x.projectId === currentProjectId && x.bygrModules.includes(BygrModule.QualityManagement)
            ),
        },
        [BygrModule.Sales]: {
            hasModule: data.some((x) => x.bygrModules.includes(BygrModule.Sales)),
            hasProject: data.some(
                (x) => x.projectId === currentProjectId && x.bygrModules.includes(BygrModule.Sales)
            ),
        },
    };
};

interface ProjectSidebarInternalLinkProps {
    children: React.ReactNode;
    to: string;
    icon: IconDefinition;
    badgeNumber?: number;
    disabled?: boolean;
    matchArray?: string[];
    className?: string;
}

const ProjectSidebarInternalLink: React.FC<ProjectSidebarInternalLinkProps> = ({
    children,
    icon,
    badgeNumber,
    className,
    to,
    matchArray,
    disabled = false,
}) => {
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    const projectId = activeProject?.id;

    const createProjectLink = (relativePart: string) => {
        return `/projects/${projectId}${relativePart}`;
    };
    const match = useRouteMatch(matchArray?.map((x) => createProjectLink(x)) ?? createProjectLink(to));

    return (
        <Menu.Item
            className={className}
            tabIndex={0}
            as={CustomLink}
            to={createProjectLink(to)}
            icon={icon}
            active={!!match}
            disabled={disabled}
            badgeNumber={badgeNumber}>
            {children}
        </Menu.Item>
    );
};

interface SellerSidebarProps { }

const SellerSidebar: React.FC<SellerSidebarProps> = (props) => {
    const activeProfile = useSelector((state: IState) => state.global.activeProfile);
    const activeProject = useSelector((state: IState) => state.global.activeProject);
    const projectOverview = useSelector((state: IState) => state.seller.projectOverview);
    const projectId = activeProject?.id;

    // const showReports = useProfileHasFeatureToggle(ProjectFeatureToggleFlags.ShowReports, activeProfile?.id) as boolean;
    const unreadMessages = useUnreadThreadCount();
    const canAccessInternalMeetings = useProjectHasFeatureToggle(ProjectFeatureToggleFlags.ShowMeetings);
    const showSurvey = useProfileHasFeatureToggle(ProfileFeatureToggleFlags.ShowProfileSurvey);

    const [canAccessExternalMeetings] = useAsyncState(
        async () => (projectId ? await hasConfig(projectId) : false),
        [projectId],
        false
    );
    const [showCustomerSurveyModal, setShowCustomerSurveyModal] = useState(false);
    const [showInsightModal, setShowInsightModal] = useState(false);
    const [unitPendingCount, setUnitPendingCount] = useState<number>(0);
    const [projectPendingCount, setProjectPendingCount] = useState<number>(0);

    const [projectMembers] = useAsyncState(() => getProjectMemberLite(), [], null);
    const projectMemberStatus = projectMembers ? getProjectMemberStatus(projectMembers, projectId) : null;

    const showMeetings =
        activeProject?.permissions.isAdministrator && (canAccessInternalMeetings || canAccessExternalMeetings);

    const canOpenReports = activeProfile?.permissions.canViewAnalytics; // && showReports;

    const canSeeOverview =
        activeProject &&
        (activeProject.permissions.isAdministrator ||
            activeProject.permissions.canViewDashboard ||
            activeProject.permissions.canEditLandingPage ||
            activeProject.permissions.canManageProject);

    return (
        <>
            <Menu mobileClassName={theme.mobileDrawer}>
                <Menu.Section type="primary">
                    <Menu.Item mobileOnly icon={faTimes}>
                        <div className={theme.mobileMenu}>
                            <Trans>Close</Trans>
                            <Icon className={theme.rubusLogo} fontScale={false} fill="#ff4843" name="logo-big" />
                        </div>
                    </Menu.Item>
                    <Menu.Item as={CustomLink} to="/projects" size="large">
                        {projectId ? (
                            <ProjectIcon
                                projectId={projectId}
                                shape="circle"
                                sizeInPx={44}
                                className={classnames(theme.projectIcon)}
                                customFallback={<Icon name="logo" className={theme.logoFallback} />}
                            />
                        ) : (
                            <Icon name="logo" className={classnames(theme.logo)} />
                        )}

                        {activeProject?.name}
                    </Menu.Item>
                    <Menu.ItemGroup active>
                        <Menu.Item
                            icon={faSwatchbook}
                        // taskDot={(projectOverview.tasks?.total ?? 0) > 0}
                        // messageDot={unreadMessages.total > 0}
                        >
                            <Trans>Change Management</Trans>
                        </Menu.Item>
                        <Menu.Item to="/projects" as={CustomLink} size="small" iconRotation={180} icon={faCorner}>
                            <Trans>All projects</Trans>
                        </Menu.Item>
                    </Menu.ItemGroup>
                    {projectMemberStatus && (
                        <>
                            {projectMemberStatus[BygrModule.Aftermarket].hasModule && (
                                <Menu.Item
                                    as="a"
                                    href={`//${window.APP_CONFIG.AFTERMARKET_URL}/${projectMemberStatus[BygrModule.Aftermarket].hasProject ? projectId : 'projects'
                                        }`}
                                    icon={faHammer}>
                                    <Trans>Aftermarket</Trans>
                                </Menu.Item>
                            )}
                            {projectMemberStatus[BygrModule.QualityManagement].hasModule && (
                                <Menu.Item
                                    as="a"
                                    href={`//${window.APP_CONFIG.QUALITY_MANAGEMENT_URL}/${projectMemberStatus[BygrModule.QualityManagement].hasProject ? projectId : 'projects'
                                        }`}
                                    icon={faPenRuler}>
                                    <Trans>Quality management</Trans>
                                </Menu.Item>
                            )}
                            {projectMemberStatus[BygrModule.Sales].hasModule && (
                                <Menu.Item
                                    as="a"
                                    href={`//${window.APP_CONFIG.SALES_URL}/${projectMemberStatus[BygrModule.Sales].hasProject ? projectId : 'projects'
                                        }`}
                                    icon={faMoneyBillWave}>
                                    <Trans>Sales</Trans>
                                </Menu.Item>
                            )}
                        </>
                    )}
                </Menu.Section>
                <Menu.Section type="default">
                    {activeProject && (
                        <>
                            {canSeeOverview && (
                                <ProjectSidebarInternalLink
                                    to={'/dashboard'}
                                    icon={faListTimeline}
                                    badgeNumber={unreadMessages[CommentThreadType.Project]}
                                    matchArray={[
                                        '/dashboard',
                                        '/landing-page',
                                        '/settings',
                                        '/users',
                                        '/project-thread',
                                        '/history-log',
                                        '/integrations',
                                    ]}>
                                    <Trans>Project</Trans>
                                </ProjectSidebarInternalLink>
                            )}

                            <ProjectSidebarInternalLink
                                to={'/residential-units'}
                                icon={faHouse}
                                badgeNumber={unreadMessages.total - unreadMessages[CommentThreadType.Project]}>
                                <Trans>Units</Trans>
                            </ProjectSidebarInternalLink>
                            <ProjectSidebarInternalLink
                                to={'/messages'}
                                icon={faCommentsAlt}
                                badgeNumber={unreadMessages.total}>
                                <Trans>Messages</Trans>
                            </ProjectSidebarInternalLink>
                            <ProjectSidebarInternalLink
                                to={'/documents'}
                                matchArray={['/documents', '/project-documents']}
                                icon={faFolders}
                                badgeNumber={
                                    (projectOverview.tasks?.documents || 0) + (projectOverview.tasks?.projectDocuments || 0)
                                }>
                                <Trans>Documents</Trans>
                            </ProjectSidebarInternalLink>
                            <ProjectSidebarInternalLink to={'/options'} icon={faBoxCheck} badgeNumber={projectOverview.tasks?.optionGroups ?? 0}>
                                <Trans>Options</Trans>
                            </ProjectSidebarInternalLink>
                            <ProjectSidebarInternalLink
                                to={'/changes'}
                                icon={faRulerCombined}
                                badgeNumber={
                                    (projectOverview.changeRequests.total ? projectOverview.changeRequests.total : 0) +
                                    unreadMessages[CommentThreadType.ChangeRequest]
                                }>
                                <Trans>Changes</Trans>
                            </ProjectSidebarInternalLink>
                            <ProjectSidebarInternalLink
                                className={theme.externalAgreementsItem}
                                to={'/external-agreements'}
                                icon={faFileSignature}
                                badgeNumber={
                                    (projectOverview.tasks?.externalAgreements ?? 0) +
                                    unreadMessages[CommentThreadType.ExternalAgreement]
                                }>
                                <Trans>Agreements</Trans>
                            </ProjectSidebarInternalLink>
                            <ProjectSidebarInternalLink
                                to={'/orders'}
                                icon={faFileInvoice}
                                badgeNumber={projectOverview.tasks?.orders}>
                                <Trans>Orders</Trans>
                            </ProjectSidebarInternalLink>
                            {showMeetings && (
                                <ProjectSidebarInternalLink to={'/meetings/overview'} icon={faHandshake}>
                                    <Trans>Meetings</Trans>
                                </ProjectSidebarInternalLink>
                            )}
                        </>
                    )}
                </Menu.Section>
                <Menu.Pusher />
                <Menu.Section type="secondary">
                    {showSurvey ? (
                        <Menu.Item as={CustomLink} to={'/reports/surveys'} icon={faQuestionSquare}>
                            <Trans>Customer surveys</Trans>
                        </Menu.Item>
                    ) : (
                        <Menu.Item disabled as="span" icon={faQuestionSquare} className={theme.upgradeButton}>
                            <Trans>Customer surveys</Trans>
                            <AddButton onClick={() => setShowCustomerSurveyModal(true)} />
                        </Menu.Item>
                    )}

                    <ProjectPermissionToggle condition={'isAdministrator'}>
                        {canOpenReports ? (
                            <Menu.Item as={CustomLink} to={'/reports/options'} icon={faChartBar}>
                                <Trans>Insights</Trans>
                            </Menu.Item>
                        ) : (
                            <Menu.Item disabled as="span" icon={faChartBar} className={theme.upgradeButton}>
                                <Trans>Insights</Trans>
                                <AddButton onClick={() => setShowInsightModal(true)} />
                            </Menu.Item>
                        )}
                    </ProjectPermissionToggle>
                    <Menu.Item as={CustomLink} to={'/catalog'} icon={faInventory}>
                        <Trans>Product catalog</Trans>
                    </Menu.Item>
                </Menu.Section>
            </Menu>
            <SellerSidebarReportsPreview isOpen={showInsightModal} toggle={() => setShowInsightModal(false)} />
            <SellerSidebarCustomerSurveyPreview
                isOpen={showCustomerSurveyModal}
                toggle={() => setShowCustomerSurveyModal(false)}
            />
        </>
    );
};

export default SellerSidebar;
